import React from 'react';
import { useTranslation } from 'react-i18next';

import { MapPin, Mail, Phone } from 'lucide-react';
import { useRotatedContact } from '../utils/contactRotation';
import { newsLocale } from '../src/news/api';
import { newsUi } from '../src/news/ui';

import Link from './LocaleLink';
const Footer: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { email: rotatedEmail, phone: rotatedPhone } = useRotatedContact();
  const telHref = `tel:${rotatedPhone.replace(/[^+\d]/g, '')}`;

  return (
    <footer className="site-footer bg-orange-900 text-orange-100 border-t border-orange-800">
      <div className="site-chrome-container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="site-footer__grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="site-footer__brand space-y-4">
            <Link to="/" className="flex items-center space-x-2 group">
              <img 
                src="/brand.png" 
                alt={i18n.language?.startsWith('ru') ? 'JH Heatsup — производитель инфракрасных обогревателей' : 'JH Heatsup - Infrared Heater Manufacturer'}
                width="160"
                height="40"
                className="h-10 w-auto object-contain" 
              />
            </Link>
            <p className="text-sm text-orange-300 leading-relaxed">
              {t('footer.brandDesc')}
            </p>
          </div>

          {/* Quick Links */}
          <div className="site-footer__products">
            <h3 className="text-white font-semibold mb-4 uppercase text-sm tracking-wider">{t('footer.products')}</h3>
            <ul className="space-y-2">
              <li><Link to="/infrared-heaters" className="hover:text-white transition-colors">{t('products.categories.infraredHeater')}</Link></li>
              <li><Link to="/portable-heaters" className="hover:text-white transition-colors">{t('products.categories.portableHeater')}</Link></li>
              <li><Link to="/gas-heaters" className="hover:text-white transition-colors">{t('products.categories.gasHeater')}</Link></li>
            </ul>
          </div>

          {/* Support */}
          <div className="site-footer__support">
            <h3 className="text-white font-semibold mb-4 uppercase text-sm tracking-wider">{t('footer.support')}</h3>
            <ul className="space-y-2">
              <li><Link to="/installation-guide" className="hover:text-white transition-colors">{t('footer.installationGuide')}</Link></li>
              <li><Link to="/installations" className="hover:text-white transition-colors">{t('installationsPage.nav')}</Link></li>
              <li><Link to="/warranty-policy" className="hover:text-white transition-colors">{t('footer.warrantyPolicy')}</Link></li>
              <li><Link to="/faq" className="hover:text-white transition-colors">{t('nav.faq')}</Link></li>
              <li><Link to="/news" className="hover:text-white transition-colors">{newsUi[newsLocale(i18n.resolvedLanguage || i18n.language)].nav}</Link></li>
              <li><Link to="/about" className="hover:text-white transition-colors">{t('nav.about')}</Link></li>
              <li><Link to="/contact" className="hover:text-white transition-colors">{t('nav.contact')}</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div className="site-footer__contact">
            <h3 className="text-white font-semibold mb-4 uppercase text-sm tracking-wider">{t('footer.contact')}</h3>
            <ul className="space-y-4">
              <li className="flex items-start">
                <MapPin className="h-5 w-5 text-orange-400 mr-3 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <span className="leading-relaxed">{t('contact.info.address')}</span>
              </li>
              <li className="flex items-start">
                <Mail className="h-5 w-5 text-orange-400 mr-3 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <a
                  href={`mailto:${rotatedEmail}`}
                  className="break-all hover:text-white transition-colors"
                >
                  {rotatedEmail}
                </a>
              </li>
              <li className="flex items-start">
                <Phone className="h-5 w-5 text-orange-400 mr-3 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <a
                  href={telHref}
                  className="hover:text-white transition-colors"
                >
                  {rotatedPhone}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="site-footer__bottom border-t border-orange-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-orange-300">
          <p>&copy; {new Date().getFullYear()} Fujian Jinghui Environmental Technology Co., Ltd. {t('footer.rights')}</p>
          <div className="site-footer__legal flex space-x-6 mt-4 md:mt-0">
            <Link to="/privacy-policy" className="hover:text-white transition-colors">{t('footer.privacy')}</Link>
            <Link to="/terms-of-service" className="hover:text-white transition-colors">{t('footer.terms')}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
